import React, { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { IC, IFontSizes, IFontWeights, IHeroNumericSx, INumericSx, IRadius } from '../insightsTheme';

/**
 * HeroMetric — the signature band of the landing.
 *
 * Real data only: actual values come from YTD rows; projection trails only when the
 * orchestrator has provided one in the series (no silent ×1.12 mock anymore).
 *
 * Props:
 *   window, setWindow      one of 'sem' | 'mes' | 'ytd' | 'y1'
 *   metric, setMetric      one of 'ben' | 'ing' | 'uds'
 *   series                 { [windowKey]: { [metricKey]: [{ d, a, f|null }] } }
 *   yoyPct                 integer %, nullable
 *   hasProjection          boolean — if false, hide forecast legend + dashed line
 *   curQ                   e.g. 'Q1'
 *   greeting               pre-translated greeting string
 */
const HeroMetric = ({
  window,
  setWindow,
  metric,
  setMetric,
  series,
  yoyPct,
  hasProjection,
  curQ = 'Q1',
  greeting,
  inactionProps, // { alertCount, agingDays, monthlyLoss } — shown as hero-rail counter
  loading = false,
}) => {
  const shimmerSx = {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.06) 25%, rgba(0,0,0,0.03) 50%, rgba(0,0,0,0.06) 75%)',
    backgroundSize: '200% 100%',
    animation: 'hero-shimmer 1.3s linear infinite',
    borderRadius: 1,
  };
  const { t } = useTranslation();
  // es-ES grouping to stay coherent with the rest of the page (ticker/levers/inaction/yoy all use
  // es-ES "356.761"); the page is a single-region (ES) operator tool, so uniform grouping > per-locale.
  const fmt = (v) => Number(v || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });

  const hd = (series && series[window] && series[window][metric]) || [];
  const lastActual = hd.filter((p) => p.a != null);
  const actualValues = lastActual.map((p) => p.a);

  // Window-scoped "headline total":
  //   sem  → most recent weekly value that is > 0 (YTD endpoint returns value=0 for the in-flight current week)
  //   mes  → sum of last ~4 weekly points (monthly total)
  //   trim → last point of cumulative series (quarter-to-date total)
  //   ytd  → sum of monthly buckets
  //   y1   → sum of quarterly buckets
  const currentVal = (() => {
    if (actualValues.length === 0) return 0;
    if (window === 'sem') {
      for (let i = actualValues.length - 1; i >= 0; i -= 1) {
        if (Number(actualValues[i]) > 0) return actualValues[i];
      }
      return actualValues[actualValues.length - 1];
    }
    if (window === 'trim') return actualValues[actualValues.length - 1];
    return actualValues.reduce((s, v) => s + Number(v || 0), 0);
  })();

  const forecastVal = hd.length > 0 ? hd[hd.length - 1].f : null;
  const unit = metric === 'uds' ? '' : '€';
  const isDown = (yoyPct || 0) < 0;
  const hasData = actualValues.length > 0 && currentVal > 0;

  const metricLabelFull = {
    ben: t('insights_v2_hero_metric_ben_full'),
    ing: t('insights_v2_hero_metric_ing_full'),
    uds: t('insights_v2_hero_metric_uds_full'),
  }[metric];

  const metricLabelShort = {
    ben: t('insights_v2_hero_metric_ben_short'),
    ing: t('insights_v2_hero_metric_ing_short'),
    uds: t('insights_v2_hero_metric_uds_short'),
  };

  const windowLabels = useMemo(
    () => [
      { k: 'sem', l: t('insights_v2_hero_window_sem') },
      { k: 'mes', l: t('insights_v2_hero_window_mes') },
      { k: 'trim', l: curQ },
      { k: 'ytd', l: t('insights_v2_hero_window_ytd') },
      { k: 'y1', l: t('insights_v2_hero_window_y1') },
    ],
    [curQ, t]
  );

  const windowLabel = useMemo(
    () =>
      ({
        sem: t('insights_v2_hero_window_weekly'),
        mes: t('insights_v2_hero_window_monthly'),
        trim: curQ,
        ytd: t('insights_v2_hero_window_ytd'),
        y1: t('insights_v2_hero_window_yearly'),
      }[window] || ''),
    [window, curQ, t]
  );

  return (
    <Box
      sx={{
        background: IC.surface,
        px: { xs: 3, md: 6 },
        pt: 3,
        pb: 2,
        borderBottom: `1px solid ${IC.bdr}`,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Eyebrow: greeting · metric · window */}
        <Typography
          sx={{
            fontSize: `${IFontSizes.sm}px`,
            color: IC.txM,
            fontWeight: IFontWeights.label,
            mb: 0.25,
          }}
        >
          {greeting && (
            <Box component="span" sx={{ color: IC.tx, fontWeight: IFontWeights.strong }}>
              {greeting}
            </Box>
          )}
          {greeting && ' · '}
          {metricLabelFull} · {windowLabel}
        </Typography>

        {/* Hero row: giant number on the left, Inaction counter on the right */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            mb: 1.5,
            flexWrap: 'wrap',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ ...shimmerSx, width: 240, height: IFontSizes.hero }} />
              <Box sx={{ ...shimmerSx, width: 80, height: 24 }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography
                component="span"
                sx={{
                  fontSize: `${IFontSizes.hero}px`,
                  fontWeight: IFontWeights.strong,
                  color: IC.tx,
                  ...IHeroNumericSx,
                  lineHeight: 1.05,
                }}
              >
                {fmt(currentVal)}
                {unit}
              </Typography>
              {yoyPct != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  clickable={false}
                  onClick={() => {}}
                  label={
                    <Box component="span" sx={{ ...INumericSx, fontWeight: IFontWeights.strong }}>
                      {yoyPct > 0 ? '+' : ''}
                      {yoyPct}% {t('insights_v2_hero_yoy')}
                    </Box>
                  }
                  sx={{
                    color: isDown ? IC.red : IC.grn,
                    borderColor: isDown ? IC.redSoft : IC.grnSoft,
                    bgcolor: isDown ? IC.redSoft : IC.grnSoft,
                    height: 26,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              )}
              {hasProjection && forecastVal != null ? (
                <Typography
                  component="span"
                  sx={{ fontSize: `${IFontSizes.sm}px`, color: IC.txM, ml: 0.5 }}
                >
                  {t('insights_v2_hero_projection_label')}{' '}
                  <Box component="strong" sx={{ color: IC.ai, fontWeight: IFontWeights.strong, ...INumericSx }}>
                    {fmt(forecastVal)}
                    {unit}
                  </Box>
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>

        {/* Toggles */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', borderBottom: `1px solid ${IC.bdrL}` }}>
            {windowLabels.map((w) => {
              const active = window === w.k;
              return (
                <Box
                  component="button"
                  key={w.k}
                  onClick={() => setWindow(w.k)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: `${IFontSizes.xs}px`,
                    fontWeight: IFontWeights.label,
                    background: 'transparent',
                    color: active ? IC.pri : IC.txM,
                    borderBottom: active ? `2px solid ${IC.pri}` : '2px solid transparent',
                    mb: '-1px',
                    fontFamily: 'inherit',
                  }}
                >
                  {w.l}
                </Box>
              );
            })}
          </Box>
          <Box
            sx={{
              display: 'flex',
              background: IC.surfaceAlt,
              borderRadius: 1,
              p: 0.25,
              border: `1px solid ${IC.bdr}`,
            }}
          >
            {['ben', 'ing', 'uds'].map((mk) => {
              const active = metric === mk;
              return (
                <Box
                  component="button"
                  key={mk}
                  onClick={() => setMetric(mk)}
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 0.75,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: `${IFontSizes.xs}px`,
                    fontWeight: IFontWeights.label,
                    background: active ? IC.pri : 'transparent',
                    color: active ? IC.onDark : IC.txM,
                    fontFamily: 'inherit',
                    transition: 'background 0.15s ease, color 0.15s ease',
                  }}
                >
                  {metricLabelShort[mk]}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Chart — full width; the Inaction rail is rendered as its own card below the hero */}
        <Box>
        {loading ? (
          <Box
            sx={{
              width: '100%',
              height: 120,
              ...shimmerSx,
              borderRadius: 1.5,
              mb: 0.5,
            }}
          />
        ) : (
        <ResponsiveContainer width="100%" height={120}>
          <ComposedChart data={hd} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="insightsV2HeroFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={IC.chartA} stopOpacity={0.18} />
                <stop offset="100%" stopColor={IC.chartA} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={IC.bdrL} vertical={false} />
            <XAxis
              dataKey="d"
              tick={{ fontSize: 10, fill: IC.txM }}
              axisLine={{ stroke: IC.bdrL }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: IC.txM }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)}
              domain={['auto', 'auto']}
            />
            <Tooltip
              contentStyle={{
                background: IC.surface,
                border: `1px solid ${IC.bdr}`,
                borderRadius: 8,
                fontSize: 11,
                color: IC.tx,
              }}
              formatter={(v, n) => {
                const labels = {
                  a: t('insights_v2_hero_legend_actual'),
                  f: t('insights_v2_hero_legend_projection'),
                };
                return [v != null ? `${fmt(v)}${unit}` : '—', labels[n] || n];
              }}
            />
            {/* "Now" divider whenever there's a trailing projection-only point */}
            {hasProjection && hd.length >= 2 && hd[hd.length - 1]?.a == null && (
              <ReferenceLine
                x={hd[hd.length - 2].d}
                stroke={IC.txM}
                strokeDasharray="2 3"
                strokeOpacity={0.6}
              />
            )}
            {hasProjection && (
              <Line
                type="monotone"
                dataKey="f"
                stroke={IC.ai}
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={{ r: 3, fill: IC.ai, stroke: IC.surface, strokeWidth: 2 }}
                connectNulls={false}
              />
            )}
            <Area
              type="monotone"
              dataKey="a"
              stroke={IC.chartA}
              strokeWidth={2.5}
              fill="url(#insightsV2HeroFill)"
              dot={{ r: 3, fill: IC.chartA, stroke: IC.surface, strokeWidth: 2 }}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        )}
        </Box>

        {!loading && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              mt: 0.5,
              fontSize: `${IFontSizes.xs}px`,
              color: IC.txM,
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 2.5, background: IC.chartA, borderRadius: 1 }} />
              {t('insights_v2_hero_legend_actual')}
            </Box>
            {hasProjection && (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 14, height: 0, borderTop: `2px dashed ${IC.ai}` }} />
                {t('insights_v2_hero_legend_projection')}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <style>{`@keyframes hero-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </Box>
  );
};

export default HeroMetric;
